import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import type { IGridItem } from "@iventis/interactive-grid";
import { fontSizes } from "@iventis/styles/src/atomic-rules";
import { Content } from "@iventis/translations/content/typed-content";
import { getContrastTextColour } from "@iventis/utilities";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import React, { MouseEventHandler, useEffect, useState } from "react";
import { styled, truncateText } from "@iventis/styles";
import { nonBreakingSpace } from "@iventis/styles/src/components/texts";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCalendarSessionColour } from "./event-block-helpers";
import { SummaryColumnGroup, SummaryRowGroup } from "./schedule.types";

const summaryColumnHeaderLowerFontSize = fontSizes.xSmall;

export const renderSummaryColumnHeader = (group: SummaryColumnGroup, timezone: string) => (
    <StyledSummaryColumnHeader>
        <div id="sum-col-head-day">Day {group?.dayNumber}</div>
        <div id="sum-col-head-date">{format(utcToZonedTime(group?.date, timezone), "EEE dd MMM")} </div>
    </StyledSummaryColumnHeader>
);

const StyledSummaryColumnHeader = styled.div`
    width: 100%;
    height: 100%;
    background-color: #c7c7c7;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    #sum-col-head-day {
        font-weight: bold;
        font-size: ${fontSizes.treeNodeLarge};
        margin-bottom: 4px;
    }

    #sum-col-head-date {
        font-size: ${summaryColumnHeaderLowerFontSize};
    }
`;

export const renderSummaryCluster = (group: SummaryRowGroup) => (
    <StyledSummaryCluster>
        <span>{group?.groupName}</span>
    </StyledSummaryCluster>
);

export const renderSummaryVenue = (group: SummaryRowGroup) => (
    <StyledSummaryVenue>
        <span className={truncateText}>{group?.groupCode}</span>
    </StyledSummaryVenue>
);

export const renderSummaryDiscipline = (group: SummaryRowGroup) => (
    <StyledSummaryDiscipline>
        <span className={truncateText}>{group?.groupCode}</span>
    </StyledSummaryDiscipline>
);

const StyledSummaryCluster = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-transform: uppercase;
    font-size: ${fontSizes.summarySmall};

    span {
        margin-left: 7px;
        font-weight: bold;
    }
`;

const StyledSummaryVenue = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: left;
    align-items: center;
    text-transform: uppercase;

    span {
        margin-left: 5px;
        font-weight: bold;
        font-size: ${fontSizes.treeNodeLarge};
    }
`;

const StyledSummaryDiscipline = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-transform: uppercase;

    span {
        margin-left: 5px;
        font-weight: normal;
        font-size: ${fontSizes.treeNodeMedium};
    }
`;

export const RenderOriginHeader = (updateParentWidth, parentWidth) => {
    const [width, setWidth] = useState(parentWidth);
    const [mouseDown, setMouseDown] = useState({ mouseDown: false, column: undefined });

    useEffect(() => {
        setWidth(parentWidth);
    }, [parentWidth]);

    const handleMouseDown = (event: React.MouseEvent<SVGElement>, column: "venue" | "discipline") => {
        setMouseDown({ mouseDown: true, column });
        event.preventDefault();
        event.stopPropagation();
    };

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const handleMouseUp = (_event: React.MouseEvent) => {
        setMouseDown({ mouseDown: false, column: undefined });
    };

    const handleMouseMove = (event: React.MouseEvent) => {
        if (mouseDown.mouseDown) {
            setWidth({ ...width, [mouseDown.column]: mouseDown.column === "discipline" ? event.pageX - width.venue : event.pageX });
            updateParentWidth(width);
        }
    };
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const translate = useIventisTranslate();
    return (
        <StyledSummaryOriginHeader onMouseUp={handleMouseUp} onMouseMove={handleMouseMove} onMouseLeave={handleMouseUp}>
            <StyledHeaderWrapper className="header-wrapper" style={{ width: `${width.venue}px` }}>
                <span>{translate(Content.schedule.summary_grid.venue).toLocaleUpperCase()}</span>
                <StyledResizeElement className="resize">
                    <FontAwesomeIcon style={{ transform: "rotate(90deg)" }} size="xs" onMouseDown={(e) => handleMouseDown(e, "venue")} icon={["fas", "grip-dots"]} />
                </StyledResizeElement>
            </StyledHeaderWrapper>
            <StyledHeaderWrapper className="header-wrapper" style={{ width: `${width.discipline}px` }}>
                <span>{`${translate(Content.schedule.summary_grid.discipline).toLocaleUpperCase().slice(0, 4)}.`}</span>
                <StyledResizeElement className="resize">
                    <FontAwesomeIcon style={{ transform: "rotate(90deg)" }} size="xs" onMouseDown={(e) => handleMouseDown(e, "discipline")} icon={["fas", "grip-dots"]} />
                </StyledResizeElement>
            </StyledHeaderWrapper>
        </StyledSummaryOriginHeader>
    );
};

const StyledResizeElement = styled.div`
    width: 20px !important;
    margin-bottom: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: grab;
`;

const StyledHeaderWrapper = styled.div`
    &.header-wrapper {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        justify-content: center;
    }
`;

const StyledSummaryOriginHeader = styled.div`
    width: 100%;
    height: 100%;
    background-color: #c7c7c7;
    display: flex;
    justify-content: stretch;
    align-items: stretch;

    font-size: ${summaryColumnHeaderLowerFontSize};

    span {
        margin-bottom: 5px;
    }

    div {
        width: 100%;
        text-align: center;
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
        align-items: center;
    }
`;

export interface IGridItemEventBlock extends IGridItem {
    eventBlock: Hierarchy<EventBlock>;
}

export const renderGridItem = (
    { id, eventBlock }: IGridItemEventBlock,
    showText: boolean,
    suppressClickEvent: boolean,
    timezone: string,
    onClick?: MouseEventHandler<HTMLDivElement>
) => {
    const { fill, outline } = getCalendarSessionColour(eventBlock.item);
    const textColour = getContrastTextColour(fill);
    return (
        <StyledGridItem
            data-cy="interactive-grid-event-block"
            key={id}
            fill={fill}
            textColour={textColour}
            clickable={Boolean(onClick)}
            outline={outline}
            onClick={(e) => !suppressClickEvent && onClick && onClick(e)}
        >
            {showText ? (
                <span>{`${format(utcToZonedTime(eventBlock.item.startAt, timezone), "HH:mm")} - ${format(utcToZonedTime(eventBlock.item.endAt, timezone), "HH:mm")}`}</span>
            ) : (
                nonBreakingSpace
            )}
        </StyledGridItem>
    );
};

const StyledGridItem = styled.div<{ outline: string; fill: string; textColour: string; clickable: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2px 3px 2px 3px;
    border-radius: 3px;
    user-select: none;
    cursor: ${(props) => (props.clickable ? "pointer" : undefined)};

    border: 1px solid ${(props) => props.outline};
    background-color: ${(props) => props.fill};
    margin: 3px 2px 0 2px;
    font-size: ${fontSizes.xSmall};
    color: ${(props) => props.textColour};
`;
